'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import posthog from 'posthog-js';

import { DRIVER_LANDING_VARIANTS } from './experiment-keys';

interface ExperimentContextValue {
  variant: string;
  experimentKey: string;
  isOverride: boolean;
}

const ExperimentContext = createContext<ExperimentContextValue | null>(null);

interface ExperimentProviderProps {
  children: React.ReactNode;
  variant: string;
  experimentKey: string;
  isOverride?: boolean;
}

/**
 * Provides experiment variant context to child components and fires
 * the PostHog $feature_flag_called exposure event on mount.
 *
 * The exposure event is required for PostHog to register that a user
 * was exposed to a particular variant, enabling experiment analysis.
 */
export function ExperimentProvider({
  children,
  variant,
  experimentKey,
  isOverride = false,
}: ExperimentProviderProps) {
  const exposureFired = useRef(false);

  useEffect(() => {
    if (exposureFired.current) return;
    exposureFired.current = true;

    posthog.capture('$feature_flag_called', {
      $feature_flag: experimentKey,
      $feature_flag_response: variant,
      ...(isOverride && { $experiment_override: true }),
    });
  }, [experimentKey, variant, isOverride]);

  const contextValue = useMemo(
    () => ({ variant, experimentKey, isOverride }),
    [variant, experimentKey, isOverride],
  );

  return (
    <ExperimentContext.Provider value={contextValue}>
      {children}
      {(process.env.NODE_ENV === 'development' || isOverride) && (
        <VariantNavigator variant={variant} isOverride={isOverride} />
      )}
    </ExperimentContext.Provider>
  );
}

/**
 * Returns the current experiment variant and flag key.
 *
 * Must be used within an ExperimentProvider. Returns null if no
 * experiment context is available (component outside experiment scope).
 */
export function useExperiment(): ExperimentContextValue | null {
  return useContext(ExperimentContext);
}

/**
 * Floating dev-only variant switcher.
 * Shows all variants as clickable pills, navigates by setting ?variant= param.
 */
function VariantNavigator({
  variant,
  isOverride,
}: {
  variant: string;
  isOverride: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-1.5 font-mono text-xs">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-fit rounded bg-yellow-400 px-2.5 py-1 text-black shadow-lg hover:bg-yellow-300"
      >
        {collapsed ? 'Variants' : 'Hide'}{' '}
        {isOverride ? `(${variant})` : `[${variant}]`}
      </button>

      {!collapsed && (
        <div className="flex flex-wrap gap-1">
          {DRIVER_LANDING_VARIANTS.map((v) => (
            <a
              key={v}
              href={`?variant=${v}`}
              className={`rounded px-2.5 py-1 shadow transition-all ${
                v === variant
                  ? 'bg-yellow-400 text-black'
                  : 'bg-black/80 text-white/80 hover:bg-black hover:text-white'
              }`}
            >
              {v}
            </a>
          ))}
          <a
            href="?variant=clear"
            className="rounded bg-red-900/80 px-2.5 py-1 text-white/80 shadow transition-all hover:bg-red-800 hover:text-white"
          >
            clear
          </a>
        </div>
      )}
    </div>
  );
}
